import { Component, HostListener, OnInit, ViewChild } from '@angular/core';
import { ChangeDetectorRef } from '@angular/core'
import { AbstractControl, FormGroup, FormBuilder, Validators, FormControl, ValidatorFn, AsyncValidatorFn } from '@angular/forms';
import { SystemService } from 'src/app/service/system.service';
import { MAT_MOMENT_DATE_FORMATS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { NO_PAGINATION } from 'src/app/model/pagination.options';
import { AdminService } from 'src/app/service/admin.service';
import { SelectOption } from 'src/app/model/select.option';
import { EventTitlePipe } from 'src/app/pipe/event.title.pipe';
import { EventsService } from 'src/app/service/events.service';
import { EventType } from 'src/app/model/eventType';
import { NotificationService } from 'src/app/service/notification.service';
import { Event } from 'src/app/model/event';
import { Country } from 'src/app/model/country';
import { TimeZone } from 'src/app/model/timeZone';
import { EventInfo } from 'src/app/model/eventInfo';
import { TimezoneService } from 'src/app/service/timezone.service';
import { Observable, forkJoin } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationDialogComponent } from 'src/app/component/confirmation-dialog/confirmation-dialog.component';
import { HasUnsavedChanges } from 'src/app/guard/unsaved-changes.guard';
import { buildXlsxBlob, XlsxRow } from 'src/app/utils/xlsx-export';
import { EVENT_ADMIN_ICONS } from '../event-admin-icons';
import { AuthService } from 'src/app/service/auth.service';

@Component({
  selector: 'app-event-settings',
  templateUrl: './event-settings.component.html',
  styleUrls: ['./event-settings.component.scss'],
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: 'pt-BR' },
    { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE] },
    { provide: MAT_DATE_FORMATS, useValue: MAT_MOMENT_DATE_FORMATS }
  ]
})
export class EventSettingsComponent implements OnInit, HasUnsavedChanges {
  /** Mapa central de ícones (template referencia `icons.settings` no header). */
  readonly icons = EVENT_ADMIN_ICONS;
  /** Flag de "submit em andamento" — passada à floating-submit-bar pra mostrar
   *  estado de loading no botão e bloquear clicks repetidos enquanto o request
   *  está em voo. */
  isSubmitting = false;
  /** Snapshot serializado dos dois forms após load/save/discard — fonte de
   *  verdade pra `isDirty()`. O flag nativo `.dirty` do reactive form marca
   *  "foi tocado" e não volta a false quando o usuário reverte o valor;
   *  comparação por conteúdo faz o botão "Salvar" desaparecer quando o
   *  usuário edita e retorna ao original. Mesma técnica de reviews-config. */
  private formsSnapshot: string | null = null;
  event: Event;
  subEvents: Array<Event>;
  subEvents$: Observable<any>;
  /** Evento COMPLETO (via `adminService.getEvent()`) — traz `tracks`,
   *  `subconferences`, `eventParent`, `eventInfo.description`, etc. numa
   *  tacada. `getEventSettings` (usado pelo form) devolve uma view enxuta;
   *  a prévia precisa da versão completa que o autor/revisor enxerga. */
  private previewFullEvent: Event | null = null;
  // MEMO do clone da prévia — sem cache, o getter retornaria objeto novo
  // a cada CD e o `<app-event-view>` interpretaria como Input mudado,
  // disparando `ngOnChanges` → `getEventChairs` → CD → loop infinito
  // (`ERR_INSUFFICIENT_RESOURCES`). Só refaz o clone quando as fontes
  // (base + subEvents) realmente mudam (comparação por referência).
  private _previewEventCache: Event | null = null;
  private _previewEventCacheKey: { base: Event | null, subs: Array<Event> | null } | null = null;

  private loadFullEventForPreview(): void {
    const obs = this.adminService.getEvent();
    if (!obs) { return; }
    obs.subscribe(fullEvent => {
      this.previewFullEvent = fullEvent || null;
    });
  }

  /** Evento a ser passado pra prévia (`<app-event-view>`). Base = evento
   *  COMPLETO (loadFullEventForPreview); enquanto ele não chega, cai no
   *  event do form como fallback (renderiza só a parte enxuta). Merge de
   *  `subEvents`/`subconferences` garante os sub-eventos fetched via
   *  getSubconferencesByEvent, caso o full não os traga. Memoizado. */
  get previewEvent(): Event | null {
    const base = this.previewFullEvent || this.event;
    const subs = this.subEvents || null;
    // Cache hit — mesmas referências das fontes → devolve o mesmo clone.
    if (this._previewEventCacheKey
        && this._previewEventCacheKey.base === base
        && this._previewEventCacheKey.subs === subs) {
      return this._previewEventCache;
    }
    if (!base) {
      this._previewEventCacheKey = { base: null, subs };
      this._previewEventCache = null;
      return null;
    }
    // Clona preservando o protótipo (Event tem getters como `formattedName`).
    const clone = Object.assign(Object.create(Object.getPrototypeOf(base)), base);
    // Sub-eventos podem vir em `subEvents` (endpoint by-id, `<app-event>`
    // lê daqui) OU `subconferences` (endpoint by-uri, `<app-event-view>`
    // lê daqui). Escolhe a primeira fonte populada, com fallback pro
    // `subEvents` fetched via `getSubconferencesByEvent`. Depois espelha
    // pros DOIS campos pra alimentar os dois consumidores.
    const pickedSubs = (clone.subEvents && clone.subEvents.length > 0) ? clone.subEvents
                     : (clone.subconferences && clone.subconferences.length > 0) ? clone.subconferences
                     : (subs && subs.length > 0) ? subs
                     : null;
    if (pickedSubs) {
      clone.subEvents = pickedSubs;
      clone.subconferences = pickedSubs;
    }
    this._previewEventCacheKey = { base, subs };
    this._previewEventCache = clone;
    return clone;
  }
  eventForm: FormGroup;
  eventTypes: EventType[];
  eventInfoForm: FormGroup;
  countries: Country[];
  timeZones: TimeZone[];
  eventInfo: EventInfo;
  eventCountryOption: Country;
  eventCountryList: Array<Country>;
  parentOption: SelectOption;
  parentList: Array<SelectOption> = [];
  enabledDisabledOption: SelectOption;
  enabledDisabledOptions: Array<SelectOption>;

  allCities: any;

  events: Array<SelectOption>;
  /* eslint-disable constructor-super */
  /* @ts-ignore */
  constructor(
    private adminService: AdminService,
    private fb: FormBuilder,
    public systemService: SystemService,
    public eventsService: EventsService,
    public notificationService: NotificationService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private timezoneService: TimezoneService,
    private translate: TranslateService,
    private dialog: MatDialog,
    private authService: AuthService
  ) {}

  /** Superusuário vê a seção "Funções extras"; chair comum não vê. Vem
   *  do endpoint dedicado /token/validate/issu/ (cacheado no AuthService).
   *  Setado async no ngOnInit; até resolver fica false e a seção some. */
  isSuperuser = false;

  /** Formato do `date` pipe pros metadados (Criado em / Última atualização)
   *  conforme o idioma corrente:
   *   • PT: `25 de jun de 2026 14:30 (UTC-03:00)`
   *   • EN: `Jun 25, 2026 14:30 (UTC-03:00)`
   *  As literais (`'de'`, `,`, `(UTC...)`) ficam entre aspas simples no
   *  padrão Angular DatePipe. */
  get metadataDateFormat(): string {
    const lang = (this.translate.currentLang || this.translate.defaultLang || 'pt').toLowerCase();
    if (lang.startsWith('en')) {
      return "MMM dd, yyyy HH:mm '(UTC'ZZZZZ')'";
    }
    return "dd 'de' MMM 'de' yyyy HH:mm '(UTC'ZZZZZ')'";
  }

  ngOnInit() {
    this.authService.directUserIsAdmin().subscribe(v => this.isSuperuser = !!v);
    setTimeout(() => {
      this.allCities = this.systemService.AllCities;
      this.timeZones = this.timezoneService.getAllTimeZones();

      this.systemService.countries.subscribe(countries => {
        this.countries = countries;
      });

      this.eventsService.getEventTypes().subscribe(types => {
        this.eventTypes = types;
      });

      this.adminService.getEventSettings().subscribe(event => {
        next: {
          this.event = event;
          this.initEventForm();
          this.initEventInfoForm();
          // Snapshot inicial = estado carregado do backend. A partir daqui
          // o `isDirty()` só é true se algum valor difere disto.
          this.captureFormsSnapshot();

          if(this.event.eventParent instanceof Event){
            this.parentOption = new SelectOption(this.event.eventParent.id, `${this.event.eventParent.acronym} - ${this.event.eventParent.name}`);
            this.parentList = new Array;
            this.parentList.push(this.parentOption);
          }
          this.eventCountryOption = new Country;
          this.eventCountryOption.isoCode = this.event.eventInfo.country;
          this.eventCountryOption.name = this.event.eventInfo.country;
          this.eventCountryList = new Array;
          this.eventCountryList.push(this.eventCountryOption);
  
          this.enabledDisabledOptions = new Array;
          this.enabledDisabledOption = new SelectOption(false, "false");
          this.enabledDisabledOptions.push(this.enabledDisabledOption);
          this.enabledDisabledOption = new SelectOption(true, "true");
          this.enabledDisabledOptions.push(this.enabledDisabledOption);
        }
        complete: {
          this.getSubEvents();
          this.loadFullEventForPreview();
        }
      });
    });
  }

  private getSubEvents(): void {
    this.subEvents$ = this.eventsService.getSubconferencesByEvent(this.event.id);
    this.subEvents$.subscribe(subEvents => {
      if (subEvents.length > 0) {
        this.subEvents = subEvents;
        this.sortEvents(this.subEvents);
      }
      // Carrega lista de candidatos a `eventParent` DEPOIS de ter os sub-eventos
      // (pra excluí-los — não pode virar pai de si mesmo, direta ou transitivamente).
      // Antes esse load ficava dentro do `app-select-input-le`; migramos p/ cá pra
      // podermos passar as opções pro `app-jems-select` (que não busca sozinho).
      this.loadParentOptions();
    });
  }

  /** Constrói a lista de eventos elegíveis a parent: todos menos o atual e
   *  seus sub-eventos, ordenados por acrônimo/ano/nome. Cada item é um
   *  `SelectOption` com `id` (internal) e `value` (label formatado). */
  private loadParentOptions(): void {
    const excludedIds = new Set<number>([this.event.id, ...(this.subEvents || []).map(s => s.id)]);
    this.eventsService.getAllEventsWithMinimumDetails().subscribe(events => {
      const opts = events
        .filter(e => !excludedIds.has(e['id']))
        .sort((a, b) => `${a.acronym} ${a.year} - ${a.name}`.localeCompare(`${b.acronym} ${b.year} ${b.name}`))
        .map(e => new SelectOption(e.id, `${e.acronym} ${e.year} - ${e.name}`));
      // Mantém a opção "atual" (parentOption) se estiver preservada mas fora do fetch.
      if (this.parentOption && !opts.some(o => o.id === this.parentOption.id)) {
        opts.unshift(this.parentOption);
      }
      this.parentList = opts;
    });
  }

  /** Prefix completo da URL do evento (`<baseUrl>/`) — adicionado ao
   *  valor do form control de `uri` pra exibição/edição protegida.
   *  Submit/validator extraem o slug antes de mandar pro backend. */
  get uriPrefix(): string { return this.baseUrl + '/'; }

  /** Extrai o SLUG da URL completa armazenada no form control de `uri`. */
  private extractUriSlug(full: string | null | undefined): string {
    const v = (full || '').toString();
    return v.startsWith(this.uriPrefix) ? v.slice(this.uriPrefix.length) : v;
  }

  /** Envelopa o `eventURIValidator` do service: o validator real espera
   *  o slug, mas o form control armazena a URL completa. Aqui passamos
   *  um AbstractControl-like com `value: slug` pro validator original. */
  private wrapUriValidator(originalSlug?: string): AsyncValidatorFn {
    const inner = this.eventsService.eventURIValidator(originalSlug);
    return (control: AbstractControl) => {
      const slug = this.extractUriSlug(control.value);
      return inner({ value: slug } as AbstractControl);
    };
  }

  private initEventForm(): void {
    this.eventForm = this.fb.group({
      name: [this.event.name, [Validators.required, Validators.minLength(1), Validators.maxLength(150)]],
      acronym: [this.event.acronym, [Validators.required, Validators.minLength(1), Validators.maxLength(30)]],
      // O form control de `uri` armazena a URL COMPLETA (prefix + slug);
      // assim o `<input>` renderiza tudo nativamente e a seleção/copy
      // funciona em qualquer parte. O validator é envelopado pra extrair
      // o slug antes de chamar o backend.
      uri: [this.uriPrefix + (this.event.uri || ''), [Validators.required], [this.wrapUriValidator(this.event.uri)]],
      year: [this.event.year],
      eventType: [this.event.eventType.id, Validators.required],
      eventParent: [this.event.eventParent instanceof Event ? this.event.eventParent.id : this.event.eventParent],
      minTopics: [this.event.minTopics],
      maxTopics: [this.event.maxTopics],
      // Os campos do IEEE (eCF, título da publicação, art source, part number)
      // NÃO moram mais aqui: o lugar deles é a aba "Transferência de direitos
      // autorais" dos Controles de publicação. Eles nunca foram exibidos nesta
      // tela — eram carregados e reenviados às cegas no submit, o que revertia,
      // com dados velhos, o que tivesse sido editado lá.
      isbn: [this.event.isbn],
      issn: [this.event.issn],
      // Funções extras (superusuário). O control existe pra todo mundo pra
      // que o snapshot de dirty-check bata; a UI só aparece pra super-user.
      tpcBulkCreateUsersEnable: [!!this.event.tpcBulkCreateUsersEnable],
      tpcBulkCreateSubmissionsEnable: [!!this.event.tpcBulkCreateSubmissionsEnable],
      tpcBulkAssignReviewsEnable: [!!this.event.tpcBulkAssignReviewsEnable]
    }, { updateOn: 'change' });

    // URI auto-derivado de acronym + year: ambos disparam o re-cálculo
    // (ex.: acronym "SBRC" + year 2025 → "sbrc2025"). Emite a URL COMPLETA
    // (prefix + slug) pra bater com o formato armazenado no form control.
    const recomputeUri = () => {
      const acronym = (this.eventForm.get('acronym')?.value || '').toString();
      const year = this.eventForm.get('year')?.value;
      const yearPart = year != null && year !== '' ? String(year) : '';
      const slug = (acronym + yearPart).replace(/\s/g, '').toLowerCase();
      this.eventForm.get('uri').setValue(this.uriPrefix + slug);
    };
    this.eventForm.get('acronym').valueChanges.subscribe(() => recomputeUri());
    this.eventForm.get('year').valueChanges.subscribe(() => recomputeUri());

    // ⚠ Normalização (lowercase / sem-espaço) NÃO acontece em
    // `valueChanges` — em cada keystroke o `setValue` resetava o cursor
    // do input pro início, impedindo edição contínua. Agora a
    // normalização é feita só no submit (`submitInfo`).

  }

  private initEventInfoForm(): void {
    this.eventInfoForm = this.fb.group({
      description: [this.event.eventInfo.description, [Validators.minLength(1), Validators.maxLength(2500)]],
      city: [this.event.eventInfo.city, [Validators.required]],
      country: [this.event.eventInfo.country, [Validators.required]],
      makePublicAt: [this.event.eventInfo.makePublicAt ?.toDate()],
      beginAt: [this.event.eventInfo.beginAt ?.toDate()],
      endAt: [this.event.eventInfo.endAt ?.toDate()],
      createdAt: [this.event.eventInfo.createdAt?.toDate()],
      updatedAt: [this.event.eventInfo.updatedAt?.toDate()],
      tpcInvitationDueAt: [this.event.eventInfo.tpcInvitationDueAt ?.toDate()],
      state: [this.event.eventInfo.state, [Validators.required, Validators.minLength(1), Validators.maxLength(100)]],
      timeZone: [this.event.eventInfo.timeZone, [Validators.required]]
    }, { updateOn: 'change' });
  }

  get baseUrl(): string {
    return window.location.origin;
  }

  /** Dirty por VALORES (não pelo flag nativo). Serializa os dois forms e
   *  compara com o snapshot capturado na última load/save/discard. `true`
   *  se algum campo hoje difere do snapshot; `false` se o usuário editou
   *  e voltou ao original. Fonte única — template e guard usam isso. */
  isDirty(): boolean {
    if (!this.eventForm || !this.eventInfoForm || this.formsSnapshot == null) { return false; }
    return this.serializeForms() !== this.formsSnapshot;
  }

  private serializeForms(): string {
    return JSON.stringify({
      ev: this.eventForm.getRawValue(),
      info: this.eventInfoForm.getRawValue(),
    });
  }

  private captureFormsSnapshot(): void {
    this.formsSnapshot = (this.eventForm && this.eventInfoForm) ? this.serializeForms() : null;
  }

  /** Required pelo `HasUnsavedChanges` — `UnsavedChangesGuard` chama
   *  no `canDeactivate` da rota; se `true`, abre dialog de confirmação
   *  antes de permitir a navegação pra fora. */
  hasUnsavedChanges(): boolean {
    return this.isDirty();
  }

  /** Listener nativo `beforeunload` — cobre fechamento da aba, refresh,
   *  navegação direta no browser (cenários que o CanDeactivate guard do
   *  Angular não captura). String customizada é ignorada por browsers
   *  modernos por segurança; basta setar `returnValue` pra disparar o
   *  prompt nativo "Deseja sair? Alterações podem ser perdidas". */
  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.hasUnsavedChanges()) {
      event.preventDefault();
      event.returnValue = '';
    }
  }

  /** Descarta alterações locais — pergunta confirmação primeiro (perder
   *  digitação não-salva por clique acidental seria ruim num form denso).
   *  Em caso de confirm, reinicializa os 2 forms a partir do `this.event`
   *  em memória. Chamado pelo `(discard)` da floating-submit-bar. */
  discardChanges(): void {
    this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'admin.event.settings.discard-changes-dialog.title',
        content: 'admin.event.settings.discard-changes-dialog.content',
        confirm: 'forms.buttons.discard',
        cancel: 'forms.buttons.cancel',
        destructive: true
      }
    }).afterClosed().subscribe(confirmed => {
      if (!confirmed) { return; }
      this.initEventForm();
      this.initEventInfoForm();
      // Descartou → os forms voltaram ao snapshot atual (baseado em
      // `this.event`); recaptura pra alinhar caso reinit tenha alterado
      // algum campo (defaults, etc.).
      this.captureFormsSnapshot();
    });
  }

  submitInfo () {
    setTimeout(() => {
    if (this.eventForm.valid && this.eventInfoForm.valid) {
      const event = new Event(this.eventForm.value.name, this.eventForm.value.acronym);
      // Form armazena a URL completa (prefix + slug) — backend só quer o
      // slug, lowercase e sem espaços (normalização feita aqui no submit;
      // valueChanges não normaliza pra não resetar o cursor a cada tecla).
      event.uri = this.extractUriSlug(this.eventForm.value.uri).replace(/\s/g, '').toLowerCase();
      event.year = this.eventForm.value.year;
      event.eventType = this.eventForm.value.eventType;
      event.eventParent = this.eventForm.value.eventParent ? this.eventForm.value.eventParent : null;

      event.minTopics = this.eventForm.value.minTopics;
      event.maxTopics = this.eventForm.value.maxTopics;

      // Sem os campos IEEE: o `EventView.put` é `partial=True`, então o que não
      // vai no payload não é tocado no banco — quem manda neles é a aba
      // "Transferência de direitos autorais".
      event.isbn = this.eventForm.value.isbn;
      event.issn = this.eventForm.value.issn;
      // Preserva a flag mesmo pra chair comum (o control existe com o valor
      // original do evento, então o put não sobrescreve com undefined).
      event.tpcBulkCreateUsersEnable = !!this.eventForm.value.tpcBulkCreateUsersEnable;
      event.tpcBulkCreateSubmissionsEnable = !!this.eventForm.value.tpcBulkCreateSubmissionsEnable;
      event.tpcBulkAssignReviewsEnable = !!this.eventForm.value.tpcBulkAssignReviewsEnable;
      
      const eventInfo = new EventInfo();
      eventInfo.description = this.eventInfoForm.value.description;
      eventInfo.city = this.eventInfoForm.value.city;
      eventInfo.state = this.eventInfoForm.value.state;
      eventInfo.country = this.eventInfoForm.value.country;
      eventInfo.timeZone = this.eventInfoForm.value.timeZone;
      eventInfo.makePublicAt = this.eventInfoForm.value.makePublicAt;
      eventInfo.beginAt = this.eventInfoForm.value.beginAt;
      eventInfo.endAt = this.eventInfoForm.value.endAt;
      eventInfo.tpcInvitationDueAt = this.eventInfoForm.value.tpcInvitationDueAt;

      // Notificações verdes "start/success" suprimidas — feedback de
      // progresso já é dado pelo `[loading]` da floating-submit-bar.
      // forkJoin emite uma vez quando os 2 requests completam; finalize
      // garante que `isSubmitting` zere mesmo em caso de erro (HTTP, etc.).
      // `editEventInfo` só roda se houver `eventInfo` no evento (caso raro
      // de evento legado sem eventInfo associado — antes quebrava no `.id`).
      this.isSubmitting = true;
      const requests: any = { ev: this.eventsService.editEvent(this.event.id, event) };
      if (this.event.eventInfo?.id) {
        requests.info = this.eventsService.editEventInfo(this.event.eventInfo.id, eventInfo);
      }
      forkJoin(requests).pipe(
        finalize(() => { this.isSubmitting = false; })
      ).subscribe((result: any) => {
        const ev = result.ev;
        this.adminService.selectedEvent = ev;
        this.event = ev;
        if (result.info) { this.event.eventInfo = result.info; }
        this.adminService.selectedEvent = this.event;
        this.eventForm.get('uri').clearAsyncValidators();
        this.eventForm.get('uri').setAsyncValidators(this.wrapUriValidator(this.event.uri));
        this.initEventInfoForm();
        // Novo snapshot pós-save — o estado salvo passa a ser o "original"
        // pra próxima rodada. `isDirty()` volta a false até nova edição real.
        this.captureFormsSnapshot();
      });

    } else {
      // markAllAsTouched dispara a renderização dos `mat-error` inline
      // abaixo dos campos com problema — feedback localizado. Removida a
      // notificação de toast (que era redundante agora que os erros
      // aparecem inline ao lado dos campos).
      this.eventForm.markAllAsTouched();
      this.eventInfoForm.markAllAsTouched();
    }

    this.validateField(this.eventForm.value.name, 'admin.event.settings.error.name-required');
        this.validateField(this.eventForm.value.acronym, 'admin.event.settings.error.acronym-required');
        this.validateField(this.eventForm.value.eventType, 'admin.event.settings.error.eventType-required');
        this.validateField(this.eventForm.value.year, 'admin.event.settings.error.year-required');
        this.validateField(this.eventForm.value.uri, 'admin.event.settings.error.uri-required');
        this.validateField(this.eventInfoForm.value.timeZone, 'admin.event.settings.error.timeZone-required');
        this.validateField(this.eventInfoForm.value.city, 'admin.event.settings.error.city-required');
        this.validateField(this.eventInfoForm.value.country, 'admin.event.settings.error.country-required');

      
    }, 50);
  } 

  public validateField(fieldValue, errorMessage) {
    if (!fieldValue) {
      this.notificationService.notifyError(errorMessage, {
        params: {
          id: this.event.id
        }
      });
    }
  }

  public setState_Country_Timezone($event): void {
    const stateCode = $event.state;
    const countryCodeAlpha2 = $event.country;
    const countryCodeAlpha3 = this.systemService.transformAlpha2ToAlpha3(countryCodeAlpha2);
    const nameCity = $event.city;
    const nameState = this.systemService.getNameStateByCodeAndCountry(stateCode, countryCodeAlpha2);
    const nameCountry = this.systemService.getNameCountryByCode(countryCodeAlpha2);

    this.eventInfoForm.controls.state.setValue(stateCode);
    
    const API_Country = this.countries.filter(country => country.isoCode === countryCodeAlpha3);
    if (API_Country.length > 0) {
      this.eventCountryOption.isoCode = API_Country[0].isoCode;
      this.eventCountryOption.name = API_Country[0].name;
      this.eventCountryList.push(this.eventCountryOption);
      this.eventInfoForm.controls.country.setValue(this.eventCountryOption.isoCode)
    }

    if (!!nameState && !!nameCountry) {
      const timezone = this.timezoneService.getTimeZone(nameCity, nameState, nameCountry);
      this.eventInfoForm.controls.timeZone.setValue(timezone);
    }
  }

  /** Exporta o evento principal + todos os sub-eventos como .xlsx (Excel)
   *  via `buildXlsxBlob`. Substituiu a versão antiga em CSV — a planilha
   *  abre direto no Excel sem aviso de formato. */
  public async generateEventInfoExport(): Promise<void> {
    if (!this.subEvents || this.subEvents.length === 0) { return; }

    const headers = ['id', 'acronym', 'year', 'name', 'jems_uri', 'parent_event'];
    const allEvents = [this.event, ...this.subEvents];
    const rows: XlsxRow[] = allEvents.map(e => ({
      values: [
        String(e.id ?? ''),
        e.acronym ?? '',
        e.year != null ? String(e.year) : '',
        e.name ?? '',
        `jems3.sbc.org.br/${e.uri ?? ''}`,
        e.eventParent != null ? String(e.eventParent) : ''
      ]
    }));

    // `numCols=[0,2,5]` → id/year/parent_event como células numéricas no Excel.
    const blob = await buildXlsxBlob('Eventos', headers, rows, [], [], [], [], [0, 2, 5]);
    const url = URL.createObjectURL(blob);
    this.downloadLink(url, `events_list.xlsx`);
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }

  private downloadLink(downloadURL: string, name: string) {
    const link = document.createElement('a');
    link.href = downloadURL;
    link.download = name;
    link.click();
  }

  public sortEvents(events: Event[]): void{
    events.sort((a,b) => a.formattedName < b.formattedName ? -1 : a.formattedName > b.formattedName ? 1 : 0);
  }
}
